import Vue from 'vue';
import { BG_EXCHANGE_RATE } from '../consts/bg-exchange-rate';
import { formatAsPrice } from '../utils';

Vue.component('configurator-edition-select', {
    props: {
        editions: {
            type: Array,
            required: true,
        },
        initialEdition: {
            type: Object,
            required: true,
        },
        noRedWithRed: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            selectionExpanded: false,
            selectedIndex: 0,
            edition: null,
            disclaimer: '',
            options: [],
            selectedOptions: [],
            showSardanaRed: true,
            parsedEditions: null,
            optionHandles: [],
            optionsAvailable: [],
            interiorOptionsExpanded: false,
            exteriorOptionsExpanded: false,
            exteriorOptionsAvailable: [],
            selectedExteriorOptions: [],
            color: null,
            paymentOption: null,
        };
    },

    computed: {
        toggleConfiguratorBoxEdition() {
            return this.selectionExpanded ? 'active' : '';
        },

        interiorOptions() {
            return this.options.filter(
                (o) => o.field_moveToSliderImage !== 'exterior',
            );
        },

        availableInteriorOptions() {
            return this.interiorOptions.filter((option) =>
                this.isInteriorOptionAvailable(option),
            );
        },

        exteriorOptions() {
            return this.options.filter(
                (o) => o.field_moveToSliderImage === 'exterior',
            );
        },

        toggleConfiguratorInteriorSelect() {
            if (this.interiorOptions.length < 1) return '';
            return this.interiorOptionsExpanded ? 'active' : '';
        },

        toggleConfiguratorExteriorSelect() {
            if (this.exteriorOptions.length < 1) return '';
            return this.exteriorOptionsExpanded ? 'active' : '';
        },

        isColorMetallic() {
            return Boolean(this.color?.field_metalic);
        },

        computedSelectedInteriorOption() {
            return this.interiorOptions.find((opt) => opt.selected) || null;
        },

        computedSelectedExteriorOption() {
            return this.exteriorOptions.find((opt) => opt.selected) || null;
        },

        shouldHideInteriorBlock() {
            if (this.interiorOptions.length !== 1) return false;
            const only = this.interiorOptions[0];
            return !!parseInt(only.field_selectedByDefault) && !!only.field_preventUnselect;
        },

        shouldHideExteriorBlock() {
            if (this.exteriorOptions.length !== 1) return false;
            const only = this.exteriorOptions[0];
            return !!parseInt(only.field_selectedByDefault) && !!only.field_preventUnselect;
        },
    },

    watch: {
        /**
         * Set edition when index is updated.
         */
        selectedIndex(value) {
            this.edition = this.editions[value];
        },

        /**
         * Send event on edition change and update options object.
         */
        edition(value) {
            // Emit edition change event
            EventBus.$emit('configurator-set-edition', value);

            try {
                this.trackEditionSelection();
            } catch (error) {
                console.error(error);
            }

            // Initialize options with default values
            this.options = value.field_commerceOptions.map((opt) => ({
                ...opt,
                selected: false,
                priceAddition: parseFloat(opt.priceValue) || 0,
            }));

            // Update available options based on color constraints
            this.checkDisabledColors();

            // Find and select the default option per type
            this.selectDefaultInteriorOption();
            this.selectDefaultExteriorOption();

            // Re-sync selection so color-select re-checks disabled colors against the new edition
            this.emitHandleExcludeInterior(this.options);

            // Update option handles for reference
            this.optionHandles = this.options.map(
                (option) => option.field_optionHandle,
            );

            // Emit event for changed options
            this.$nextTick(() => {
                EventBus.$emit(
                    'configurator-set-edition-options',
                    this.options,
                );
            });
        },
    },

    mounted() {
        this.initializeEdition();
        this.setupEventListeners();
    },

    methods: {
        formatPriceInBg(price) {
            const priceInNumbers = parseFloat(
                String(price).replace(' ', '').replace('.', ''),
            );
            const priceInBg = priceInNumbers * BG_EXCHANGE_RATE;
            return formatAsPrice(priceInBg);
        },

        /**
         * Initialize the component with the initial edition
         */
        initializeEdition() {
            this.edition = this.initialEdition;

            // Find the index of the initial edition
            const editionIndex = this.editions.findIndex(
                (edition) =>
                    edition.field_commerceHandle ===
                    this.initialEdition.field_commerceHandle,
            );

            if (editionIndex !== -1) {
                this.selectedIndex = editionIndex;
            }

            EventBus.$emit('configurator-set-edition', this.initialEdition);
        },

        /**
         * Set up all event listeners
         */
        setupEventListeners() {
            this.setupPriceAdditionListener();
            this.setupPaymentOptionListener();
            this.setupColorChangeListener();
        },

        /**
         * Listen for price addition events
         */
        setupPriceAdditionListener() {
            EventBus.$on(
                'configurator-option-price-addition',
                (
                    optionHandle,
                    price,
                    formattedPrice,
                    shopPrice,
                    showPriceImpact,
                ) => {
                    const optionIndex = this.options.findIndex(
                        (option) => option.field_optionHandle === optionHandle,
                    );

                    if (optionIndex === -1) return;

                    // Update price in option object
                    const updatedOption = { ...this.options[optionIndex] };
                    updatedOption.priceAddition = '';

                    if (price && showPriceImpact) {
                        updatedOption.priceAddition = formattedPrice;
                        updatedOption.shopPriceAddition = shopPrice;
                    }

                    // Use Vue.set to ensure reactivity
                    Vue.set(this.options, optionIndex, updatedOption);
                },
            );
        },

        /**
         * Listen for payment option changes
         */
        setupPaymentOptionListener() {
            EventBus.$on('configurator-set-payment-option', (paymentOption) => {
                this.disclaimer =
                    paymentOption.field_paymentOption_editionDisclaimer || '';
                this.paymentOption = paymentOption;
            });
        },

        /**
         * Check if an interior option has a non-zero price in the current payment option.
         */
        hasOptionPrice(handle) {
            const option = this.paymentOption?.optionsPrices?.find(
                (opt) => opt.field_optionHandle === handle,
            );

            if (!option) return false;

            const price = this.isColorMetallic
                ? option.field_priceImpactMetallic
                : option.field_priceImpactNonMetallic;

            return parseFloat(price) !== 0;
        },

        /**
         * Get the formatted price for an interior option, respecting the current metallic selection.
         */
        getOptionPriceForDisplay(handle) {
            const option = this.paymentOption?.optionsPrices?.find(
                (opt) => opt.field_optionHandle === handle,
            );

            if (!option) return '';

            const price = this.isColorMetallic
                ? parseFloat(option.field_priceImpactMetallic)
                : parseFloat(option.field_priceImpactNonMetallic);

            return formatAsPrice(price);
        },

        /**
         * Listen for color change events
         */
        setupColorChangeListener() {
            EventBus.$on('configurator-set-color', (color) => {
                this.color = color;
                this.checkDisabledColors();
                this.handleSardanaRedVisibility(color);
            });
        },

        /**
         * Handle special case for Sardana Red interior with red exterior colors
         */
        handleSardanaRedVisibility(color) {
            const isRedExterior = ['phantom-red', 'red'].includes(
                color.colorSlug,
            );
            const shouldHideSardanaRed = isRedExterior && this.noRedWithRed;

            // Update visibility flag
            this.showSardanaRed = !shouldHideSardanaRed;

            if (!shouldHideSardanaRed) {
                return;
            }

            // Find and deselect red interior if it's currently selected
            const redOption = this.options.find(
                (opt) => opt.field_optionHandle === 'redinterior',
            );

            if (!redOption || !redOption.selected) {
                return;
            }

            redOption.selected = false;

            // Remove from selected options array
            const redIndex = this.selectedOptions.indexOf('redinterior');
            if (redIndex !== -1) {
                this.selectedOptions.splice(redIndex, 1);
            }

            // Emit change event
            this.emitOptionChange(redOption);

            // Select a new default option since red is now unavailable
            this.selectDefaultInteriorOption();
            this.selectDefaultExteriorOption();
        },

        /**
         * Selects the default interior option based on availability and CMS settings
         */
        selectDefaultInteriorOption() {
            let defaultSelectedHandle = null;

            for (let opt of this.interiorOptions) {
                const isDefault = !!parseInt(opt.field_selectedByDefault);

                if (isDefault && !defaultSelectedHandle && this.isInteriorOptionAvailable(opt)) {
                    opt.selected = true;
                    defaultSelectedHandle = opt.field_optionHandle;
                } else {
                    opt.selected = false;
                }
            }

            this.selectedOptions = defaultSelectedHandle
                ? [defaultSelectedHandle]
                : [];
        },

        /**
         * Selects the default exterior option based on availability and CMS settings
         */
        selectDefaultExteriorOption() {
            const defaultHandles = [];

            for (let opt of this.exteriorOptions) {
                const isDefault = !!parseInt(opt.field_selectedByDefault);

                if (isDefault && this.isExteriorOptionAvailable(opt)) {
                    opt.selected = true;
                    defaultHandles.push(opt.field_optionHandle);
                } else {
                    opt.selected = false;
                }
            }

            this.selectedExteriorOptions = defaultHandles;
        },

        /**
         * Checks if an interior option is available based on current color and configuration
         */
        isInteriorOptionAvailable(option) {
            if (!option) return false;

            // Check for Sardana Red special case
            if (
                option.field_optionHandle &&
                option.field_optionHandle.toLowerCase().includes('sardana') &&
                !this.showSardanaRed
            ) {
                return false;
            }

            return this.optionsAvailable.includes(option.field_optionHandle);
        },

        /**
         * Checks if an exterior option is available
         */
        isExteriorOptionAvailable(option) {
            if (!option) return false;

            return this.exteriorOptionsAvailable.includes(
                option.field_optionHandle,
            );
        },

        /**
         * Handles selection of a single interior option
         */
        handleSingleInteriorSelect(selectedOption) {
            if (!this.isInteriorOptionAvailable(selectedOption)) return;

            const currentlySelected = this.interiorOptions.find((opt) => opt.selected);
            const isAlreadySelected =
                currentlySelected?.field_optionHandle ===
                selectedOption.field_optionHandle;

            // Block deselecting if the selected option is locked
            if (isAlreadySelected && currentlySelected?.field_preventUnselect) return;

            // Toggle off if clicking the already-selected option
            if (isAlreadySelected) {
                currentlySelected.selected = false;
                this.selectedOptions = [];
                this.emitOptionChange(selectedOption);
                this.emitHandleExcludeInterior(this.options);
                return;
            }

            // Otherwise switch selection
            this.interiorOptions.forEach((opt) => {
                opt.selected =
                    opt.field_optionHandle ===
                    selectedOption.field_optionHandle;
            });

            this.selectedOptions = [selectedOption.field_optionHandle];

            this.emitOptionChange(selectedOption);
            this.emitHandleExcludeInterior(this.options);
        },

        /**
         * Handles selection of a single exterior option.
         * Toggles the option — clicking a selected option deselects it.
         */
        handleSingleExteriorSelect(selectedOption) {
            if (!this.isExteriorOptionAvailable(selectedOption)) return;

            const isAlreadySelected = this.selectedExteriorOptions.includes(
                selectedOption.field_optionHandle,
            );

            // If already selected and locked, block deselection
            if (isAlreadySelected && selectedOption.field_preventUnselect) return;

            selectedOption.selected = !isAlreadySelected;

            this.selectedExteriorOptions = isAlreadySelected
                ? this.selectedExteriorOptions.filter(
                      (h) => h !== selectedOption.field_optionHandle,
                  )
                : [...this.selectedExteriorOptions, selectedOption.field_optionHandle];

            this.emitOptionChange(selectedOption);
            this.emitHandleExcludeInterior(this.options);
        },

        /**
         * Checks if an interior color option exists in available options
         */
        checkIfInteriorColorExists(optionHandle) {
            return this.optionsAvailable.includes(optionHandle);
        },

        /**
         * Updates the available interior options based on color constraints
         */
        checkDisabledColors() {
            // Interior options start fully available; exterior options are never color-restricted
            this.optionsAvailable = this.interiorOptions.map(
                (option) => option.field_optionHandle,
            );
            this.exteriorOptionsAvailable = this.exteriorOptions.map(
                (option) => option.field_optionHandle,
            );

            // Apply color-based restrictions to interior options if applicable
            if (this.color && this.color.field_disableInteriorColors_gqcddajo) {
                try {
                    const parsedColorsWithEditions = JSON.parse(
                        this.color.field_disableInteriorColors_gqcddajo,
                    );

                    parsedColorsWithEditions.forEach((colorWithEdition) => {
                        const colorWithEditionValue =
                            Object.values(colorWithEdition)[0];
                        const separatorIndex = colorWithEditionValue.indexOf('-');

                        if (separatorIndex === -1) return;

                        const disabledEdition = colorWithEditionValue.slice(
                            0,
                            separatorIndex,
                        );
                        const disabledColor = colorWithEditionValue.slice(
                            separatorIndex + 1,
                        );

                        if (
                            disabledEdition.toLowerCase() ===
                            this.edition.title.toLowerCase()
                        ) {
                            const indexToRemove =
                                this.optionsAvailable.indexOf(disabledColor);
                            if (indexToRemove !== -1) {
                                this.optionsAvailable.splice(indexToRemove, 1);
                            }
                        }
                    });
                } catch (error) {
                    console.error('Error parsing color restrictions:', error);
                }
            }
        },

        /**
         * Parse edition features from JSON
         */
        parseEditionFeatures(index) {
            return JSON.parse(this.editions[index].field_commerceFeatures);
        },

        /**
         * Returns CSS class for selected switch button
         */
        toggleSwitchButton(index) {
            return this.selectedIndex === index
                ? 'switch-button--selected'
                : '';
        },

        /**
         * Returns CSS class for selected switch text
         */
        toggleConfiguratorSwitchSelect(index) {
            return this.selectedIndex === index
                ? 'configurator-switch-select__text--visible'
                : '';
        },

        /**
         * Handle edition selection and animate height transition
         */
        handleSetSelectedEdition(index) {
            const allEditionTexts = document.querySelectorAll(
                '#configurator-edition__text',
            );

            // Collapse current selection
            if (allEditionTexts[this.selectedIndex]) {
                allEditionTexts[this.selectedIndex].style.height = 0;
            }

            // Update selection and expand new selection
            this.selectedIndex = index;

            if (allEditionTexts[this.selectedIndex]) {
                allEditionTexts[this.selectedIndex].style.height = 'auto';
            }
        },

        /**
         * Legacy method for option selection - redirects to single select handler
         * @deprecated Use handleSingleInteriorSelect instead
         */
        onOptionSelectionChange(optionParam) {
            // For safety, check if this is an interior option
            const isInterior = this.options.find(
                (o) => o.field_optionHandle === optionParam.field_optionHandle,
            );

            if (!isInterior) return;

            this.handleSingleInteriorSelect(optionParam);
        },

        /**
         * Emit events when an option's selection state changes
         */
        emitOptionChange(option) {
            EventBus.$emit('configurator-set-edition-option', option);
            EventBus.$emit('configurator-set-edition-options', this.options);
        },

        /**
         * Emit event for excluding interior options
         */
        emitHandleExcludeInterior(options) {
            EventBus.$emit('configurator-set-exclude-interior', options);
        },

        /**
         * Track edition selection in analytics
         */
        trackEditionSelection() {
            // Determine pixel ID based on site
            const pixelIds = {
                mgMotorsNetherlands: '3161292',
                mgMotorsNorway: '3161321',
            };

            const pixelId = pixelIds[this.$parent.siteHandle] || '';

            if (!pixelId) return;
        },

        getLastEditionDisclaimerClass(index) {
            return index === this.editions.length - 1
                ? 'configurator-edition__features-disclaimer--no-bottom-margin'
                : '';
        },
    },
});
